import { v4 as uuid } from 'uuid';
import { memoize1 } from 'data/function';
import './chatbotEditor.scss';
import { Delete } from './nodes/nodeMessageReceiver';

export const DEFAULT_STATE = 'default';

const prop = (name) => (obj) => obj[name];

const sortByPriority = (receivers = []) =>
    receivers.sort((a, b) => (a.priority || 0) - (b.priority || 0));
const fillInId = (receivers = []) =>
    receivers.map((r) => (r.id ? r : { ...r, id: uuid() }));

/**
 * @param {Array<MessageReceiver>}
 * @return sorted states list
 */
const topoSortStates = (messageReceivers = []) => {
    const receiversByState = messageReceivers.groupBy(prop('state'));

    const isInitialState = (s) =>
        typeof s === 'undefined' || s === DEFAULT_STATE;

    const mostInitialStates = (() => {
        const inCounts = messageReceivers
            .filter(
                (r) =>
                    typeof r.outState === 'string' && !isInitialState(r.state),
            )
            .map(prop('outState'))
            .reduce(
                (counts, s) => ({ ...counts, [s]: (counts[s] || 0) + 1 }),
                {},
            );

        const initialCounts = messageReceivers
            .filter(
                (r) =>
                    typeof r.outState === 'string' && isInitialState(r.state),
            )
            .map(prop('outState'))
            .reduce(
                (counts, s) => ({ ...counts, [s]: (counts[s] || 0) + 1 }),
                {},
            );

        return [
            ...Object.keys({ ...initialCounts, ...inCounts }).sort(
                (ka, kb) =>
                    (initialCounts[ka] || 0) > (initialCounts[kb] || 0)
                        ? 1
                        : (inCounts[ka] || 0) > (inCounts[kb] || 0) ? 1 : -1,
            ),
            DEFAULT_STATE,
            undefined,
        ];
    })();

    // Iterative depth-first search starting on state `startState` because
    // ES6 via Babel still doesn't support tail-call optimization...
    const sortedStates = (function dfs(receiversByState, startStates) {
        let stack = startStates instanceof Array ? startStates : [startStates];
        let states = [];
        const visitedStates = {};

        while (stack.length > 0) {
            const state = stack.last;
            const receivers = receiversByState[state] || [];
            stack.pop();

            if (visitedStates[state]) continue;

            states = [...states, state];

            const outStates = receivers
                .map(prop('outState'))
                .filter((s) => typeof s === 'string' && !visitedStates[s]);
            const outStatesCounts = outStates.reduce(
                (counts, s) => ({ ...counts, [s]: (counts[s] || 0) + 1 }),
                {},
            );
            stack = [
                ...stack,
                ...outStates.sort(
                    (a, b) => outStatesCounts[a] - outStatesCounts[b],
                ),
            ];

            visitedStates[state] = true;
        }

        states.unreached = Object.keys(receiversByState).filter(
            (s) => !visitedStates[s],
        );

        return states;
    })(receiversByState, mostInitialStates);

    return [
        undefined,
        DEFAULT_STATE,
        ...sortedStates,
        ...sortedStates.unreached,
    ];
};

//
export default {
    require: {
        ngModel: 'ngModel',
    },
    template: `
    <div class="state-receivers" ng-repeat="(state, receivers) in $ctrl.sortedMessageReceiversByState track by $index">
        <div class="node" ng-repeat="messageReceiver in receivers track by messageReceiver.id">
            <node-message-receiver ng-model="messageReceiver"
                ng-change="$ctrl.updateMessageReceiver(messageReceiver)"
                states="$ctrl.states"
                ></node-message-receiver>
        </div>
    </div>
    `,
    controller: class {
        constructor($scope) {
            'ngInject';

            $scope.$on(Delete, (e, { id }) => {
                this._deleteReceiver(id);
            });
        }

        updateMessageReceiver(messageReceiver) {
            this._updateModel({
                messageReceivers: this.messageReceivers.map(
                    (r) => (r.id === messageReceiver.id ? messageReceiver : r),
                ),
            });
        }

        getUniqueStates = memoize1((messageReceivers = []) =>
            messageReceivers
                .reduce((states, r) => [...states, r.state, r.outState], [])
                .nub()
                .filter((s) => typeof s === 'string'),
        );
        get states() {
            this.statesOuterScope = this.getUniqueStates(
                this._model.messageReceivers,
            );
            return this.statesOuterScope;
        }

        getMessageReceiversWithId = memoize1(fillInId);
        get messageReceivers() {
            return this.getMessageReceiversWithId(this._model.messageReceivers);
        }

        groupMessageReceiversBySortedStates = memoize1(
            (messageReceivers = []) => {
                const sortedStates = topoSortStates(messageReceivers);
                const receiversByState = this.getMessageReceiversWithId(
                    messageReceivers,
                ).groupBy(prop('state'));

                return sortedStates.reduce(
                    (receivers, s) => ({
                        ...receivers,
                        [s]: sortByPriority(receiversByState[s] || []),
                    }),
                    {},
                );
            },
        );
        get sortedMessageReceiversByState() {
            return this.groupMessageReceiversBySortedStates(
                this._model.messageReceivers,
            );
        }

        get _model() {
            return this.ngModel.$viewValue || {};
        }

        _updateModel(value) {
            this.ngModel.$setViewValue({ ...this._model, ...value });
        }

        _deleteReceiver(id) {
            this._updateModel({
                messageReceivers: this.messageReceivers.filter(
                    (r) => r.id !== id,
                ),
            });
        }
    },
    controllerAs: '$ctrl',
    bindings: {
        statesOuterScope: '=states',
    },
};
